import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  LocationOn,
  Work,
  AccessTime,
  AttachMoney,
  School,
  CheckCircle,
  CalendarToday,
  People,
  Business,
  Computer,
  Star
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isCandidate } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applicationDialog, setApplicationDialog] = useState(false);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    expectedSalary: '',
    availableDate: ''
  });
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    if (isCandidate) {
      checkApplicationStatus();
    }
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5000/api/jobs/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setJob(response.data.data);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/applications/my-applications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const applications = response.data.data || [];
      const applied = applications.some(app => app.job?._id === id || app.job === id);
      setHasApplied(applied);
    } catch (err) {
      console.error('Error checking application status:', err);
    }
  };

  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!isCandidate) {
      setError('Only candidates can apply for jobs');
      return;
    }

    // Check if profile has the mandatory fields (excluding resume since it's only required during initial save)
    // If they've saved their profile once, they would have had to upload a resume at that time
    if (!user?.profile?.firstName || 
        !user?.profile?.lastName || 
        !user?.profile?.phone ||
        !user?.profile?.address?.city ||
        !user?.profile?.address?.state) {
      setError('');
      setApplicationDialog(false);
      
      // Show alert dialog instead
      const confirmRedirect = window.confirm(
        'Your profile is incomplete. Please complete your basic profile information (name, phone, city, state) before applying to jobs.\n\nWould you like to complete your profile now?'
      );
      
      if (confirmRedirect) {
        navigate('/profile');
      }
      return;
    }

    setApplicationDialog(true);
  };

  const submitApplication = async () => {
    try {
      setApplying(true);
      const token = localStorage.getItem('token');
      
      const candidateProfile = {
        ...user.profile,
        coverLetter: applicationData.coverLetter,
        expectedSalary: applicationData.expectedSalary,
        availableDate: applicationData.availableDate
      };

      const response = await axios.post(
        `http://localhost:5000/api/jobs/${id}/apply`,
        { candidateProfile },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setHasApplied(true);
      setApplicationDialog(false);
      setError('');
      
      // Check if the candidate was auto-progressed to Technical Assessment
      const application = response.data.data;
      if (application?.currentStage === 'Technical Assessment') {
        // Show success message and redirect to dashboard
        alert('Application submitted successfully! You have been automatically progressed to the Technical Assessment stage. You will now be redirected to your dashboard.');
        navigate('/dashboard');
      } else {
        // Show success for normal flow and redirect to dashboard
        alert('Application submitted successfully! You can track your application status in the Applications page.');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error applying to job:', err);
      setError(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatSalary = (salaryRange) => {
    if (!salaryRange || !salaryRange.min || !salaryRange.max) return 'Not Disclosed';
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: salaryRange.currency || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `${formatter.format(salaryRange.min)} - ${formatter.format(salaryRange.max)}`;
  };

  const getExperienceLabel = (level) => {
    const labels = {
      entry: 'Entry Level (0-2 years)',
      mid: 'Mid Level (2-5 years)',
      senior: 'Senior Level (5-8 years)',
      lead: 'Lead (8-12 years)',
      executive: 'Executive (12+ years)'
    };
    return labels[level] || level;
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      'full-time': 'Full Time',
      'part-time': 'Part Time',
      'contract': 'Contract',
      'internship': 'Internship',
      'temporary': 'Temporary'
    };
    return labels[type] || type;
  };

  const getWorkModeLabel = (mode) => {
    const labels = {
      'onsite': 'On-site',
      'remote': 'Remote',
      'hybrid': 'Hybrid'
    };
    return labels[mode] || mode;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !job) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container>
        <Alert severity="warning">Job not found</Alert>
      </Container>
    );
  }

  const isDeadlinePassed = new Date(job.applicationDeadline) < new Date();
  const isJobActive = job.status === 'active';
  const canApply = isCandidate && !hasApplied && !isDeadlinePassed && isJobActive;

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                {job.title}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  icon={<Business />}
                  label={job.department}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<LocationOn />}
                  label={job.location}
                  variant="outlined"
                />
                <Chip
                  icon={<Work />}
                  label={getJobTypeLabel(job.jobType)}
                  variant="outlined"
                />
                <Chip
                  icon={<Computer />}
                  label={getWorkModeLabel(job.workMode)}
                  variant="outlined"
                />
              </Box>

              {!isJobActive && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This job is currently {job.status}
                </Alert>
              )}

              {isDeadlinePassed && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The application deadline for this position has passed
                </Alert>
              )}

              {hasApplied && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  You have already applied for this position
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Job Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Job Description
              </Typography>
              <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-wrap' }}>
                {job.description}
              </Typography>
            </Box>

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Key Responsibilities
                </Typography>
                <List>
                  {job.responsibilities.map((resp, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={resp} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Requirements */}
            {job.requirements && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Requirements
                </Typography>
                
                {job.requirements.education && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <School sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Education
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.requirements.education}
                    </Typography>
                  </Box>
                )}

                {job.requirements.skills && job.requirements.skills.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Star sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Required Skills
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {job.requirements.skills.map((skill, index) => (
                        <Chip key={index} label={skill} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Benefits
                </Typography>
                <List>
                  {job.benefits.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3, position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Job Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Experience Level
                  </Typography>
                  <Typography variant="body2">
                    {getExperienceLabel(job.experienceLevel)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Salary Range
                  </Typography>
                  <Typography variant="body2">
                    {formatSalary(job.salaryRange)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Number of Openings
                  </Typography>
                  <Typography variant="body2">
                    {job.openings} position{job.openings > 1 ? 's' : ''}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Application Deadline
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(job.applicationDeadline)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Posted On
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(job.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {canApply ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleApply}
                  startIcon={<Work />}
                >
                  Apply Now
                </Button>
              ) : hasApplied ? (
                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  fullWidth
                  disabled
                >
                  Already Applied
                </Button>
              ) : !isCandidate ? (
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/manage-jobs')}
                >
                  Manage Jobs
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  disabled
                >
                  Applications Closed
                </Button>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Application Dialog */}
      <Dialog open={applicationDialog} onClose={() => setApplicationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Application</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Cover Letter"
              multiline
              rows={4}
              value={applicationData.coverLetter}
              onChange={(e) => setApplicationData({ ...applicationData, coverLetter: e.target.value })}
              placeholder="Tell us why you're interested in this position..."
              fullWidth
            />
            <TextField
              label="Expected Salary (Monthly in INR)"
              type="number"
              value={applicationData.expectedSalary}
              onChange={(e) => setApplicationData({ ...applicationData, expectedSalary: e.target.value })}
              placeholder="Enter your expected monthly salary"
              fullWidth
            />
            <TextField
              label="Available From"
              type="date"
              value={applicationData.availableDate}
              onChange={(e) => setApplicationData({ ...applicationData, availableDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialog(false)}>Cancel</Button>
          <Button
            onClick={submitApplication}
            variant="contained"
            disabled={applying}
          >
            {applying ? <CircularProgress size={24} /> : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JobDetails;
